/**
 * URL からテナントを読み取る（PRODUCT_DEV-421 / PRODUCT_DEV-641）
 *
 * ## なぜ1ファイルに集めるか
 *
 * 同じ正規表現が `packages/shared-ui/loaders/auth-loader.ts`（認証ポータルへ渡すパスを
 * 組む側）と `apps/unizo/web/src/loaders/root-loader.ts`（認証済みなら `/` へ戻す側）に
 * **コメントごと重複していた**。片方だけ直すと「ポータルへは tenant 付きで飛ぶのに
 * 戻しが効かない」（あるいはその逆）が静かに起きる。**判定は必ずここを通す。**
 */

/**
 * 先頭パスセグメントが tenantId かどうか。
 *
 * パターンは `prefix-suffix` で**サフィックスに数字を含む**もの。既存のルート名
 * （`release-notes` / `meeting-groups` 等）は数字を含まないので衝突しない。
 * **この条件を緩めると実ルートがテナント扱いされて画面が出なくなる。**
 */
export const TENANT_ID_REGEX = /^([a-z0-9]+-[a-z0-9]*[0-9][a-z0-9]*)(\/|$)/i;

/**
 * パスの先頭セグメントから tenantId を取り出す。テナントでなければ null。
 *
 * 先頭の `/` は落として判定する。`/` そのものや空文字は null。
 */
export const tenantIdFromPathname = (pathname: string): string | null => {
  const firstSegment = pathname.replace(/^\//, "");
  const matched = firstSegment.match(TENANT_ID_REGEX);
  // `noUncheckedIndexedAccess` 下ではキャプチャ参照が `string | undefined` になる
  // （正規表現にグループが 1 つあることは型からは分からない）。**unizo の tsconfig は
  // このオプションを持たないので unizo だけ緑になり、dev-lab / admin の `tsc -b` が落ちる。**
  return matched?.[1] ?? null;
};

/**
 * 認証ポータルへ渡すパス。`"/"` か `"/<tenantId>/"`。
 *
 * ## `fixedTenantId` があるときに `"/"` を返す理由
 *
 * 配信ホストでテナントが決まる環境（PRODUCT_DEV-641）では、**認証ポータル側も
 * 同じ環境のビルドなので自分が誰のポータルか知っている**。URL に tenantId を
 * 載せても行き先は同じ画面で、載せた分だけ「パスの値」と「ビルド時の値」という
 * 正本が2つになる。載せない。
 */
export const authPortalTenantPath = (args: {
  pathname: string;
  /** 配信ホストで決まるテナント。無い環境では未指定 or 空文字。 */
  fixedTenantId?: string | null;
}): string => {
  if (args.fixedTenantId) return "/";
  const tenantId = tenantIdFromPathname(args.pathname);
  return tenantId ? `/${tenantId}/` : "/";
};
